// components/schema/GraphPanel.jsx

import React, { useEffect, useRef, useState } from "react"
import ForceGraph2D from "react-force-graph-2d"
import {
  drawNodeWithInnerLabel,
  paintNodePointerArea,
  formatNodeTooltip,
  formatLinkTooltip,
  getNodeColor,
} from "./graph-utils"

export default function GraphPanel({
  graphData,
  mode,              // "add-edge" | null
  edgeSourceId,      // string | null
  onAddNodeClick,
  onAddEdgeClick,
  onNodeClick,
}) {
  const graphRef = useRef(null)
  const panelRef = useRef(null)
  const [panelSize, setPanelSize] = useState({ w: 600, h: 520 })

  useEffect(() => {
    const el = panelRef.current
    if (!el) return

    let raf = 0
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const w = Math.max(320, Math.floor(rect.width))
        const h = Math.max(360, Math.floor(rect.height))
        setPanelSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }))
      })
    })

    ro.observe(el)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!graphRef.current) return
    const fg = graphRef.current
    const linkForce = fg.d3Force("link")
    if (linkForce) linkForce.distance(() => 140)
    const charge = fg.d3Force("charge")
    if (charge) charge.strength(-350)
    fg.d3ReheatSimulation()
  }, [graphData])

  const zoomBy = (k = 1.2) => {
    const fg = graphRef.current
    if (!fg) return
    const curr = typeof fg.zoom === "function" ? fg.zoom() : 1
    fg.zoom(curr * k, 400)
  }
  const zoomToFit = () => graphRef.current?.zoomToFit?.(600, 80)
  const centerAt = () => graphRef.current?.centerAt?.(0, 0, 400)

  return (
    <div className="lg:col-span-5 col-span-12 sticky top-6 self-start h-[calc(100vh-7rem)]">
      <div
        ref={panelRef}
        className="relative h-full rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800"
      >
        {/* Canvas toolbar */}
        <div className="absolute top-3 left-3 z-10 flex gap-2">
          <button
            type="button"
            onClick={onAddNodeClick}
            className="px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm shadow-sm hover:bg-emerald-700"
          >
            + Node
          </button>
          <button
            type="button"
            onClick={onAddEdgeClick}
            className={`px-3 py-1.5 rounded-md text-sm shadow-sm border
              ${mode === "add-edge"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"}`}
          >
            + Edge
          </button>
        </div>

        {/* Mode hint */}
        {mode === "add-edge" && (
          <div className="absolute top-3 right-3 z-10 rounded-md bg-black/70 text-white px-3 py-1.5 text-xs">
            {edgeSourceId
              ? `Select target node for edge from "${edgeSourceId}"`
              : "Click a node to select edge source"}
          </div>
        )}

        {/* Zoom controls */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          <button
            onClick={() => zoomBy(1 / 1.2)}
            className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            -
          </button>
          <button
            onClick={() => zoomBy(1.2)}
            className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            +
          </button>
          <button
            onClick={zoomToFit}
            className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Fit
          </button>
          <button
            onClick={centerAt}
            className="px-3 py-1.5 rounded bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Center
          </button>
        </div>

        <ForceGraph2D
          ref={graphRef}
          graphData={graphData}
          width={panelSize.w}
          height={panelSize.h}
          nodeRelSize={4}
          nodeResolution={16}
          nodeLabel={(n) => formatNodeTooltip(n)}
          nodeColor={(n) => getNodeColor(n.id)}
          linkColor={() => "rgba(100,100,100,0.5)"}
          linkWidth={1}
          linkDirectionalArrowLength={5}
          linkDirectionalArrowRelPos={0.94}
          linkLabel={(l) => formatLinkTooltip(l)}
          linkCurvature={(link) => (link.source === link.target ? 0.6 : 0)}
          d3AlphaDecay={0.03}
          d3VelocityDecay={0.5}
          enableZoomPanInteraction={true}
          minZoom={0.1}
          maxZoom={8}
          nodeCanvasObject={drawNodeWithInnerLabel}
          nodePointerAreaPaint={paintNodePointerArea}
          onNodeClick={onNodeClick}
          linkCanvasObjectMode={() => "after"}
          linkCanvasObject={(link, ctx, globalScale) => {
            if (!link.type) return

            const s = link.source
            const t = link.target
            if (!s || !t || s.x == null || t.x == null) return

            const mx = (s.x + t.x) / 2
            const my = (s.y + t.y) / 2

            const label = String(link.type)
            const fontSize = 11 / globalScale

            const prevAlpha = ctx.globalAlpha
            ctx.globalAlpha = 1

            ctx.font = `${fontSize}px 'Inter', sans-serif`
            ctx.textAlign = "center"
            ctx.textBaseline = "middle"

            const textWidth = ctx.measureText(label).width
            const padX = 6, padY = 3

            const darkMode = document.documentElement.classList.contains("dark")
            const bgColor = darkMode ? "rgba(31,41,55,0.9)" : "rgba(255,255,255,0.95)"
            const borderColor = darkMode ? "rgba(255,255,255,0.15)" : "rgba(17,24,39,0.1)"
            const textColor = darkMode ? "#f9fafb" : "#111827"

            ctx.fillStyle = bgColor
            ctx.strokeStyle = borderColor
            ctx.lineWidth = 1 / globalScale
            ctx.beginPath()
            ctx.roundRect(
              mx - textWidth / 2 - padX,
              my - fontSize / 2 - padY,
              textWidth + padX * 2,
              fontSize + padY * 2,
              6 / globalScale
            )
            ctx.fill()
            ctx.stroke()

            ctx.fillStyle = textColor
            ctx.fillText(label, mx, my)

            ctx.globalAlpha = prevAlpha
          }}
        />
      </div>
    </div>
  )
}
